// Kluis client-side crypto. ALL encryption/decryption happens here, in the browser. The password
// and plaintext NEVER leave the page; the server only ever receives the opaque outputs.
//
// Scheme (envelope encryption, one password per vault):
//   password --PBKDF2-HMAC-SHA256(salt, iterations)--> KEK (AES-256-GCM)
//   KEK --wraps--> random 256-bit vault key (AES-256-GCM)
//   vault key --encrypts--> each entry ({ v, title, body } JSON, AES-256-GCM, fresh IV per write)
//
// The wrapped vault key doubles as the password verifier: unwrapping fails (AES-GCM auth) when the
// password is wrong, so unlockVault() returns null = "not your usual password". No separate
// password hash/canary is stored. KDF params are stored per vault so the cost can be raised later.

const KDF_NAME = "pbkdf2-sha256";
// ~600k PBKDF2-HMAC-SHA256 iterations: a deliberate per-guess cost so a stolen DB can't be
// brute-forced cheaply, while a single unlock stays well under a second.
const PBKDF2_ITERATIONS = 600_000;

export type KdfParams = { name: string; iterations: number; hashLen: number };
export const DEFAULT_KDF_PARAMS: KdfParams = { name: KDF_NAME, iterations: PBKDF2_ITERATIONS, hashLen: 32 };

export type VaultCrypto = {
  kdfName: string;
  kdfParams: KdfParams;
  kdfSalt: string;
  wrappedKeyIv: string;
  wrappedKeyCiphertext: string;
};
export type EntryPlain = { title: string; body: string };

const enc = new TextEncoder();
const dec = new TextDecoder();

// UTF-8 encode into a fresh ArrayBuffer-backed view (TextEncoder.encode is typed ArrayBufferLike,
// which WebCrypto's BufferSource rejects; the copy pins it to ArrayBuffer).
function utf8(s: string): Uint8Array<ArrayBuffer> {
  return new Uint8Array(enc.encode(s));
}

// AAD: authenticated (not secret) purpose tags so a wrapped-key blob can't be passed off as an
// entry blob and vice versa.
const AAD_WRAP = utf8("kluis:v1:wrapped-vault-key");
const AAD_ENTRY = utf8("kluis:v1:entry");

// ── base64 (binary only) ──
function bytesToB64(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}
function b64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function randomBytes(n: number): Uint8Array<ArrayBuffer> {
  return crypto.getRandomValues(new Uint8Array(n));
}

async function deriveKek(password: string, salt: Uint8Array<ArrayBuffer>, params: KdfParams): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey("raw", utf8(password.normalize("NFC")), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: params.iterations, hash: "SHA-256" },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

async function aesEncrypt(key: CryptoKey, plaintext: Uint8Array<ArrayBuffer>, aad: Uint8Array<ArrayBuffer>): Promise<{ iv: string; ciphertext: string }> {
  const iv = randomBytes(12); // fresh 96-bit IV per write; never reused
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: aad, tagLength: 128 }, key, plaintext),
  );
  return { iv: bytesToB64(iv), ciphertext: bytesToB64(ct) };
}

async function aesDecrypt(key: CryptoKey, ivB64: string, ctB64: string, aad: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: b64ToBytes(ivB64), additionalData: aad, tagLength: 128 },
    key,
    b64ToBytes(ctB64),
  );
  return new Uint8Array(pt);
}

async function importVaultKey(raw: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  // Non-extractable: it can be used to en/decrypt this session but not exported back to raw bytes.
  return crypto.subtle.importKey("raw", raw, "AES-GCM", false, ["encrypt", "decrypt"]);
}

/** First-time setup: from a chosen password, produce the server-stored crypto material + the live
 *  vault key to start encrypting with. */
export async function setupVault(password: string): Promise<{ crypto: VaultCrypto; vaultKey: CryptoKey }> {
  const salt = randomBytes(32);
  const params = DEFAULT_KDF_PARAMS;
  const kek = await deriveKek(password, salt, params);
  const rawVaultKey = randomBytes(32);
  const wrapped = await aesEncrypt(kek, rawVaultKey, AAD_WRAP);
  const vaultKey = await importVaultKey(rawVaultKey);
  rawVaultKey.fill(0); // best-effort scrub of the raw key bytes
  return {
    crypto: {
      kdfName: params.name,
      kdfParams: params,
      kdfSalt: bytesToB64(salt),
      wrappedKeyIv: wrapped.iv,
      wrappedKeyCiphertext: wrapped.ciphertext,
    },
    vaultKey,
  };
}

/** Unlock: derive the KEK from the entered password + stored salt/params, unwrap the vault key.
 *  Returns null when the password is wrong (AES-GCM auth failure) — the verifier. */
export async function unlockVault(password: string, vault: VaultCrypto): Promise<CryptoKey | null> {
  const kek = await deriveKek(password, b64ToBytes(vault.kdfSalt), vault.kdfParams);
  try {
    const raw = await aesDecrypt(kek, vault.wrappedKeyIv, vault.wrappedKeyCiphertext, AAD_WRAP);
    const key = await importVaultKey(raw);
    raw.fill(0); // best-effort scrub
    return key;
  } catch {
    return null; // wrong password
  }
}

/** Password change: verify the current password, then re-wrap the SAME vault key under the new one
 *  (fresh salt). Entries do not need re-encryption. Returns null when the current password is wrong. */
export async function rekeyVault(
  currentPassword: string,
  newPassword: string,
  vault: VaultCrypto,
): Promise<VaultCrypto | null> {
  const currentKek = await deriveKek(currentPassword, b64ToBytes(vault.kdfSalt), vault.kdfParams);
  let rawVaultKey: Uint8Array<ArrayBuffer>;
  try {
    rawVaultKey = await aesDecrypt(currentKek, vault.wrappedKeyIv, vault.wrappedKeyCiphertext, AAD_WRAP);
  } catch {
    return null; // wrong current password
  }
  const salt = randomBytes(32);
  const params = DEFAULT_KDF_PARAMS;
  const newKek = await deriveKek(newPassword, salt, params);
  const wrapped = await aesEncrypt(newKek, rawVaultKey, AAD_WRAP);
  rawVaultKey.fill(0); // best-effort scrub
  return {
    kdfName: params.name,
    kdfParams: params,
    kdfSalt: bytesToB64(salt),
    wrappedKeyIv: wrapped.iv,
    wrappedKeyCiphertext: wrapped.ciphertext,
  };
}

export async function encryptEntry(vaultKey: CryptoKey, plain: EntryPlain): Promise<{ iv: string; ciphertext: string }> {
  const json = JSON.stringify({ v: 1, title: plain.title, body: plain.body });
  return aesEncrypt(vaultKey, utf8(json), AAD_ENTRY);
}

export async function decryptEntry(vaultKey: CryptoKey, ivB64: string, ctB64: string): Promise<EntryPlain> {
  const bytes = await aesDecrypt(vaultKey, ivB64, ctB64, AAD_ENTRY);
  const obj = JSON.parse(dec.decode(bytes)) as { title?: unknown; body?: unknown };
  return { title: String(obj.title ?? ""), body: String(obj.body ?? "") };
}
